import config from "./config.js";
import Utils from "./libs/snake.lib.utils.js";
import Hook from "./libs/snake.lib.hook.js";
import Trace from "./libs/snake.lib.trace.js";
import Identity from "./libs/snake.lib.identity.js";
import Storage from "./libs/snake.lib.storage.js";
import AnalyticsService from "./libs/snake.lib.analytics.js";
import Component from "./component.js";
import ApiService from "./libs/snake.lib.api.js";

export default class ComponentLoad {
    static modalI18nGenericOptions;
    static subscriptionAlreadyPurchased = false;

    static init = async () => {
        Trace.debug("Component _cmpLoad");

        Utils.spinner("loader", true);

        this.modalI18nGenericOptions = Component.getModalI18nGenericOptions();

        Component.load(config.routing.sections.agent.component);

        if (Identity.isAuth()) {
            Trace.warn("cmp load isAuth");

            ApiService.getDictionary()
                .then((dictionary) => {
                    Trace.debug("getDictionary response");
                    Trace.log(dictionary);

                    window.snakeCheckout.dictionary = dictionary?.data?.content[window.snakeCheckout.currentCountry] || [];

                    Trace.debug(window.snakeCheckout.dictionary);
                })
                .catch((e) => {
                    Trace.debug("getDictionary error");
                    Trace.debug(e);

                    AnalyticsService.errorEvent("0_init", "technical", "snakeAPI | get dictionary error", e);
                });

            if (window.snakeCheckout.type === "subscription" && !Identity.isAgent() && !Identity.isAgentCustomer()) {
                await this.#checkDoubleOrder(window.snakeCheckout.initData.products[0]);

                if (this.subscriptionAlreadyPurchased) {
                    this.#showDoubleOrderModal();

                    await this.#customerSignOut(false, "gproxy | sign-out double order error");

                    Utils.spinner("loader", false);
                }
            }

            if (!this.subscriptionAlreadyPurchased) {
                await this.#customerAccountInfoInit();

                /**
                 * Listener to intercept the Sign Out user action
                 */
                Hook.subscribe("_signOut", () => {
                    AnalyticsService.checkoutSignOutEvent("icon");

                    Utils.modal(
                        "dialog",
                        true,
                        { text: window.snakeCheckout.translations["profile.signout.dialog.text"], confirmBtn: window.snakeCheckout.translations["profile.signout.dialog.confirm"], dismissBtn: window.snakeCheckout.translations["profile.signout.dialog.dismiss"] },
                        async () => {
                            Utils.modal("dialog", false);
                            Utils.spinner("loader", true);

                            AnalyticsService.checkoutSignOutEvent("exit");

                            await this.#customerSignOut(true, "gproxy | sign-out error");
                        },
                        () => {
                            AnalyticsService.checkoutSignOutEvent("continue_checkout");
                        },
                        "bold"
                    );
                });

                document.querySelector("#sign-out")?.classList.remove("hidden");

                Utils.spinner("loader", false);

                Component.load(config.routing.sections.profile.component);
                Trace.warn("AFTER PROFILE");
            }
        } else {
            Utils.spinner("loader", false);

            Component.load(config.routing.sections.profile.component);
        }
    };

    static #checkDoubleOrder = async (product) => {
        await Identity.purchaseLookup(product)
            .then((response) => {
                this.subscriptionAlreadyPurchased = response;
            })
            .catch((e) => {
                Utils.modal("popup", true, this.modalI18nGenericOptions);

                AnalyticsService.errorEvent("0_init", "technical", "gproxy | purchase lookup error", e);
            });
    };

    static #showDoubleOrderModal = () => {
        const redirect = (uri = null) => {
            setTimeout(() => {
                window.location.href = import.meta.env.CO_BASE_DOMAIN + "/" + window.snakeCheckout.currentCountry + (uri ? "/" + uri : "");
            }, 500);
        };

        Utils.modal(
            "dialog",
            true,
            { title: window.snakeCheckout.translations["doubleorder.dialog.title"], text: window.snakeCheckout.translations["doubleorder.dialog.message"].replace("%s", window?.snakeCheckout?.initData?.products[0]?.name ?? ""), confirmBtn: window.snakeCheckout.translations["doubleorder.dialog.purchaseagain"], dismissBtn: window.snakeCheckout.translations["doubleorder.dialog.gotomyarea"] },
            () => {
                Utils.modal("dialog", false);

                Utils.modal(
                    "dialog",
                    true,
                    { title: window.snakeCheckout.translations["doubleorder.dialog.title"], text: window.snakeCheckout.translations["doubleorder.dialog.contacts"].replace("%s", import.meta.env.CO_BASE_DOMAIN + "/" + window.snakeCheckout.currentCountry), confirmBtn: window.snakeCheckout.translations["modal.close"], dismissBtn: window.snakeCheckout.translations["doubleorder.dialog.gotomyarea"] },
                    () => {
                        Utils.modal("dialog", false);
                        Utils.spinner("loader", true);

                        redirect();
                    },
                    () => {
                        Utils.modal("dialog", false);
                        Utils.spinner("loader", true);

                        redirect("myarea");
                    }
                );
            },
            () => {
                Utils.spinner("loader", true);

                redirect("myarea");
            }
        );
    };

    static #customerAccountInfoInit = async () => {
        Trace.warn("customerAccountInfoInit");

        await Identity.getAccountInfo()
            .then(async (accountInfoResponse) => {
                if (accountInfoResponse.errorCode !== 0) {
                    Utils.modal("popup", true, this.modalI18nGenericOptions);

                    AnalyticsService.errorEvent("0_init", "technical", "gproxy | get user account info error", accountInfoResponse);
                } else {
                    Trace.warn("customerAccountInfoInit response");
                    Trace.log(accountInfoResponse);

                    let updateAddresses = false;

                    let shippingAddresses = Array.isArray(accountInfoResponse.data.shippingAddress) ? accountInfoResponse.data.shippingAddress : [];
                    let billingAddress = accountInfoResponse.data.billingAddress || {};

                    const addresses = this.#handleRemovedNormStreetNumField(shippingAddresses, billingAddress);

                    shippingAddresses = addresses[0];
                    billingAddress = addresses[1];

                    /**
                     * Get user shipping addresses related to current country
                     */
                    const shippingAddressesOnCurrentCountry = shippingAddresses.filter((address) => address.normCountryCode === config.countries[window.snakeCheckout.currentCountry].countryCodeGigya);

                    /**
                     * Get user shipping addresses related to other countries
                     */
                    const shippingAddressesOnOtherCountries = shippingAddresses.filter((address) => address.normCountryCode !== config.countries[window.snakeCheckout.currentCountry].countryCodeGigya);

                    /**
                     * We check if the user has at least one shipping address for the current country
                     */
                    if (shippingAddressesOnCurrentCountry.length !== 0) {
                        let defaultShippingAddressIndex = shippingAddressesOnCurrentCountry.findIndex((element) => element.defaultAddress);

                        /**
                         * We check if the user has a default shipping address for the current country.
                         * If the default shipping address is missing we take the first address in the list.
                         */
                        if (defaultShippingAddressIndex === -1) {
                            shippingAddressesOnCurrentCountry[0].defaultAddress = true;

                            defaultShippingAddressIndex = 0;

                            updateAddresses = true;
                        }

                        /**
                         * We check if the user has a saved billing address
                         */
                        if (Object.keys(billingAddress).length === 0) {
                            /**
                             * If the user doesn't have a billing address, we set the default shipping address as the billing address
                             */
                            billingAddress = shippingAddressesOnCurrentCountry[defaultShippingAddressIndex];

                            updateAddresses = true;
                        }

                        if (updateAddresses) {
                            const allShippingAddresses = [shippingAddressesOnCurrentCountry];
                            shippingAddressesOnOtherCountries.forEach((address) => allShippingAddresses.push(address));

                            /**
                             * Update the addresses on Gigya
                             */
                            await Identity.setAddresses(allShippingAddresses, billingAddress)
                                .then(async (setAddressesResponse) => {
                                    Trace.warn("customerAccountInfoInit Identity.setAddresses");
                                    Trace.log(setAddressesResponse);

                                    if (setAddressesResponse.errorCode !== 0) {
                                        Utils.modal("popup", true, this.modalI18nGenericOptions);

                                        AnalyticsService.errorEvent("0_init", "technical", "gproxy | set addresses response error", setAddressesResponse);
                                    } else {
                                        Trace.warn("customerAccountInfoInit setaddresses");
                                        Trace.log(setAddressesResponse);

                                        /**
                                         * Update the addresses on BigCommerce
                                         */
                                        await ApiService.updateAddresses(accountInfoResponse.profile.email, shippingAddressesOnCurrentCountry[defaultShippingAddressIndex], billingAddress).catch((e) => {
                                            Utils.modal("popup", true, this.modalI18nGenericOptions);

                                            AnalyticsService.errorEvent("0_init", "technical", "snakeAPI | commerce update addresses error", e);
                                        });
                                    }
                                })
                                .catch((e) => {
                                    Utils.modal("popup", true, this.modalI18nGenericOptions);

                                    AnalyticsService.errorEvent("0_init", "technical", "gproxy | set addresses error", e);
                                });
                        }
                    }

                    Trace.warn("customerAccountInfoInit pre account-info");

                    window.snakeCheckout.accountInfo = {
                        gigyaId: accountInfoResponse.UID || null,
                        // UIDSignature: accountInfoResponse.UIDSignature || null,
                        data: {
                            commerceId: accountInfoResponse.data.commerceId || null,
                            shippingAddressesOnOtherCountries,
                            shippingAddresses: shippingAddressesOnCurrentCountry,
                            billingAddress,
                        },
                        profile: accountInfoResponse.profile || {},
                    };

                    Trace.warn("customerAccountInfoInit post account-info");
                    Trace.log(window.snakeCheckout.accountInfo);
                }
            })
            .catch(async (e) => {
                /**
                 *  If error 403005 "Unauthorized user"
                 *
                 *  TODO: ask why on SignIn, even if the credentials are right, when we try to retrieve the use infos we get a 403005 error but we are logged in anyways
                 *
                 *  If error 401 "the token is expired"
                 *
                 *  TODO: ask why, even if window.gproxy.session.valid() is true, we get a 401 error
                 */
                if (e.errorCode === 403005 || e.errorCode === 401) {
                    await this.#customerSignOut(false, "gproxy | get user account info sign-out error");
                } else {
                    Utils.modal("popup", true, this.modalI18nGenericOptions);

                    AnalyticsService.errorEvent("0_init", "technical", "gproxy | get user account info error", e);
                }
            });
    };

    static #handleRemovedNormStreetNumField = (shippingAddresses, billingAddress) => {
        const countries = ["CZE", "GRC", "ITA", "POL", "SVK", "GBR"];

        /**
         * FIX needed for the removal of the "normStreetNum" field from the shipping form
         * for "cz", "gr", "it", "pl", "sk" and "uk" countries
         */
        shippingAddresses.forEach((address, index) => {
            if (countries.includes(address.normCountryCode || "") && (address.normStreetNum || false)) shippingAddresses[index].addressLine1 = shippingAddresses[index].addressLine1.trim() + " " + (shippingAddresses[index].normStreetNum || "");
        });

        /**
         * FIX needed for the removal of the "normStreetNum" field from the billing form
         * for "cz", "gr", "it", "pl", "sk" and "uk" countries
         */
        if (countries.includes(billingAddress.normCountryCode || "") && (billingAddress.normStreetNum || false)) billingAddress.addressLine1 = billingAddress.addressLine1.trim() + " " + (billingAddress.normStreetNum || "");

        return [shippingAddresses, billingAddress];
    };

    static #customerSignOut = async (reload = true, errorMessage = "") => {
        await Identity.signOut()
            .then(() => {
                Storage.clear(() => {
                    if (reload) window.location.reload();
                });
            })
            .catch((e) => {
                /**
                 *  If error 403007 "Invalid namespace &#39;accounts&#39; or method &#39;logout&#39; or you do not have the required permissions to call it." + "Permission denied"
                 *
                 *  TODO: ask why
                 */
                if (e.errorCode === 403007) {
                    Storage.clear(() => {
                        if (reload) window.location.reload();
                    });
                } else {
                    Utils.modal("popup", true, this.modalI18nGenericOptions);

                    AnalyticsService.errorEvent("0_init", "technical", errorMessage, e);
                }
            });
    };
}
